import React from "react";
import { ORB_HUES } from "../theme/materials";
import { getThemeMode } from "../theme/colors";

// I² Visual Identity v1 — R1.3 AmbientOrbs (2026-04-29).
//
// Drop-in decorative layer. Renders 1-4 large soft-blurred orbs at fixed
// positions in the viewport that drift slowly via CSS keyframes. The
// ORB_HUES palette swaps with the theme so the same component yields
// the dark-vibrant or light-pastel look from the user's reference
// mockups (visionOS / Linear / 2025-dashboard).
//
// Why this matters: glass on a flat color looks unfinished. The orbs +
// gradient backdrop together are what gives the references their depth.
// Per design doc §1 last paragraph + §5.3.
//
// CSS-only — no JS animation loop, no GPU expense beyond the standard
// `filter: blur()` cost (composite layer per orb). 4 orbs = 4 composite
// layers, well under the budget on any modern GPU.
//
// Honors `prefers-reduced-motion` — orbs render statically with no drift
// animation when the user opts out at the OS level. Per design §7.10.
//
// Props:
//   count     — 1..4. Defaults to 4.
//   intensity — 0..1 multiplier on orb opacity. Defaults to 0.6 (dark)
//               / 0.4 (light). Light theme runs lower because pastel
//               orbs on cream backdrop need less saturation to read.
//
// Mounting:
//   Place once at the root of any layout that wants the ambient look.
//   <AmbientOrbs /> uses position:fixed + zIndex:0 + pointerEvents:none
//   so it sits behind every interactive element without intercepting
//   clicks. Glass surfaces (zIndex >= 1) blur this layer on top.
//
// NOT mounted in Shell.jsx by default — adoption is per-surface (per
// the three-rings ladder in the design doc). Login + Library landing
// (R2.1+R2.2 batched) are the first consumers.

export default function AmbientOrbs({ count = 4, intensity }) {
  const mode = getThemeMode();
  const hues = ORB_HUES[mode] || ORB_HUES.dark;
  // Moderation pass 2026-04-29 (post-polish-overproduction): pulled
  // back from 0.85/0.55 to 0.45/0.30. The polish-pass defaults read
  // as "candy" — references show MODERATE saturation: vivid orbs in
  // corners, not full-viewport haze. Goal: orbs visible, not dominant.
  const effectiveIntensity = intensity != null
    ? intensity
    : (mode === "dark" ? 0.45 : 0.30);

  // Four orbs (was 5; dropped the 5th hot-highlight — it added clutter
  // without depth). Positioned so coverage feels intentional but not
  // overwhelming. Single drift cycle (60s, ease-in-out) — three cycles
  // in the polish pass made the surface feel busy.
  const orbs = [
    { hue: 0, top: "-15%", left: "-8%",  size: "55vmax", blur: "100px", delay: "0s" },
    { hue: 1, top: "55%",  left: "60%",  size: "45vmax", blur: "90px",  delay: "-15s" },
    { hue: 2, top: "30%",  left: "-15%", size: "40vmax", blur: "85px",  delay: "-30s" },
    { hue: 3, top: "-10%", left: "70%",  size: "38vmax", blur: "85px",  delay: "-45s" },
  ].slice(0, Math.max(1, Math.min(4, count)));

  // Convert intensity (0..1) to hex alpha pair.
  const alphaFor = () => Math.round(Math.min(1, effectiveIntensity) * 255)
    .toString(16)
    .padStart(2, "0");

  return (
    <div
      data-testid="ambient-orbs"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 0,
        overflow: "hidden",
      }}
    >
      {orbs.map((o, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: o.top,
            left: o.left,
            width: o.size,
            height: o.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${hues[o.hue]}${alphaFor()}, transparent 70%)`,
            filter: `blur(${o.blur})`,
            animation: "i2-orb-drift 60s ease-in-out infinite",
            animationDelay: o.delay,
          }}
        />
      ))}
      <style>{`
        @keyframes i2-orb-drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33%      { transform: translate(6vw, -4vh) scale(1.04); }
          66%      { transform: translate(-4vw, 4vh) scale(0.96); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes i2-orb-drift {
            0%, 100% { transform: translate(0, 0) scale(1); }
          }
        }
      `}</style>
    </div>
  );
}
